function _extends() { return _extends = Object.assign ? Object.assign.bind() : function (n) { for (var e = 1; e < arguments.length; e++) { var t = arguments[e]; for (var r in t) ({}).hasOwnProperty.call(t, r) && (n[r] = t[r]); } return n; }, _extends.apply(null, arguments); }
import React, { useEffect, useMemo, useState } from 'react';
import { Autocomplete, getOptionLabel, getOptionSelected, TextField, Typography } from 'seagull';
import { Controller, useFormContext } from 'react-hook-form';
import { requiredValidation, titleValidation } from 'validators';
import { connectControllerToTextField, genericConnector } from "../../../utils/react-hook-form.utils";
import styles from "../BillClientModal.module.scss";
import { serviceTemplateModel } from "../../../models";
import { getUnitName } from "../../../utils/unit-name.utils";
import DiscountTextField from "../../DiscountTextField";
import { utils } from "../../../utils";
import { useOnMount } from "../../../hooks/useOnMount";
import { useServiceTemplateApi } from "../../../hooks/useServiceTemplateApi";
import BillingItemNote from "../BillingNote/BillingItemNote";
import { getServiceTemplateOptions, getTableInfo, validateGreaterThan0 } from "./BillChargeTableItem.utils";
import BillTableItem from "../BillTableItem/BillTableItem";
import { InitialDiscount } from "../BillClientModal.types";
import { getTotal } from "../BillClientModal.utils";
import { BillChargeClientApprovalCallout } from "../Callouts/Callouts";
export default function BillChargeTableItem(props) {
  const {
    invoiceDateInfo,
    newUI,
    actionColumn,
    itemIndex,
    chargeItem
  } = props;
  const {
    trigger,
    control,
    resetField,
    watch,
    setValue
  } = useFormContext();
  const serviceTemplateApi = useServiceTemplateApi();
  const [serviceTemplates, setServiceTemplates] = useState([]);
  const [serviceTemplatesError, setServiceTemplatesError] = useState();
  const [loadingServiceTemplates, setLoadingServiceTemplates] = useState(true);
  const [selectedService, setSelectedService] = useState(null);
  const [issueDate] = watch(['issueDate']);
  const {
    service,
    quantity,
    rate,
    discount
  } = chargeItem.charge;
  const {
    columnDefs,
    headerItems
  } = useMemo(() => getTableInfo(discount), [discount]);
  const total = useMemo(() => {
    return getTotal(quantity, rate, service, discount);
  }, [quantity, rate, service, discount]);
  useEffect(() => {
    // The error in discount field depends on total amount so we trigger change detection when total changes.
    trigger(["items.".concat(itemIndex, ".charge.discount")]);
  }, [total]);
  const serviceTemplateOptions = useMemo(() => {
    return getServiceTemplateOptions(serviceTemplates, loadingServiceTemplates);
  }, [serviceTemplates, loadingServiceTemplates]);
  useOnMount(() => {
    serviceTemplateApi.get({
      includeAdhocCharges: true,
      includeDeleted: false
    }).then(services => {
      setServiceTemplates(services.raw);
      setLoadingServiceTemplates(false);
    }).catch(err => setServiceTemplatesError(err));
  });
  function applyServiceOptionChange(newValue) {
    const serviceId = newValue === null || newValue === void 0 ? void 0 : newValue.value;
    const service = serviceId ? serviceTemplates.find(s => s.id === serviceId) : undefined;
    setSelectedService(service || null);
    if (service) {
      setValue("items.".concat(itemIndex, ".charge.quantity"), serviceTemplateModel.isFixedPrice(service.cost) ? '1' : '', {
        shouldValidate: true
      });
      setValue("items.".concat(itemIndex, ".charge.rate"), serviceTemplateModel.price(service).abs().toString(), {
        shouldValidate: true
      });
      const discount = serviceTemplateModel.discount(service);
      setValue("items.".concat(itemIndex, ".charge.discount"), discount ? {
        type: discount.type(),
        amount: discount.amount().toString()
      } : InitialDiscount);
    } else {
      resetField("items.".concat(itemIndex, ".charge.quantity"));
      resetField("items.".concat(itemIndex, ".charge.rate"));
      setValue("items.".concat(itemIndex, ".charge.discount"), InitialDiscount);
    }
  }
  const rowItems = [{
    value: /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "items.".concat(itemIndex, ".charge.service"),
      rules: titleValidation('', {
        required: true
      }),
      render: renderProps => /*#__PURE__*/React.createElement(Autocomplete, {
        selectOnFocus: true,
        freeSolo: true,
        size: "small",
        "data-testid": "add-charge-".concat(itemIndex, "-service-selector"),
        inputValue: renderProps.field.value ? renderProps.field.value.label : '',
        textFieldProps: {
          inputRef: renderProps.field.ref,
          error: genericConnector(renderProps).showErrors
        },
        loading: loadingServiceTemplates,
        options: serviceTemplateOptions,
        getOptionLabel: getOptionLabel,
        getOptionSelected: getOptionSelected,
        onBlur: renderProps.field.onBlur,
        onInputChange: (_, value, reason) => {
          if (reason === 'clear') {
            applyServiceOptionChange(null);
            renderProps.field.onChange(null);
            return;
          }
          if (reason !== 'input') return; // Other reasons are picked up by onChange handler.
          const option = serviceTemplateOptions.find(s => s.label === value);
          const fallbackServiceOptionValue = {
            value: '',
            label: value
          };
          applyServiceOptionChange(option || fallbackServiceOptionValue);
          renderProps.field.onChange(option || fallbackServiceOptionValue);
        },
        onChange: (event, value) => {
          const newValue = value ? value : null;
          applyServiceOptionChange(newValue);
          renderProps.field.onChange(newValue);
        }
      })
    })
  }, {
    value: /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "items.".concat(itemIndex, ".charge.quantity"),
      rules: {
        validate: validateGreaterThan0
      },
      render: renderProps => /*#__PURE__*/React.createElement(TextField, _extends({
        isMoney: true,
        size: "small",
        "data-testid": "add-charge-quantity-".concat(itemIndex),
        inputProps: {
          min: 0
        },
        disabled: !service
      }, connectControllerToTextField(renderProps, {
        noHelperText: true
      })))
    })
  }, {
    value: /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "items.".concat(itemIndex, ".charge.rate"),
      rules: requiredValidation(true),
      render: renderProps => /*#__PURE__*/React.createElement("div", {
        className: styles.rate
      }, /*#__PURE__*/React.createElement(TextField, _extends({
        isMoney: true,
        size: "small",
        "data-testid": "add-charge-".concat(itemIndex, "-rate"),
        inputProps: {
          min: 0
        },
        disabled: !service
      }, connectControllerToTextField(renderProps, {
        noHelperText: true
      }))), selectedService && serviceTemplateModel.isVariablePrice(selectedService.cost) && /*#__PURE__*/React.createElement(Typography, {
        ellipsis: true,
        className: styles.units,
        variant: "paragraph2",
        skin: "secondary",
        "data-testid": "add-charge-".concat(itemIndex, "-units")
      }, "/", getUnitName(serviceTemplateModel.unitName(selectedService))))
    })
  }, {
    value: /*#__PURE__*/React.createElement(Controller, {
      control: control,
      name: "items.".concat(itemIndex, ".charge.discount"),
      rules: {
        validate: value => value && total != null && total.lessThan(0) ? 'The discount cannot be greater than the rate' : true
      },
      render: renderProps => /*#__PURE__*/React.createElement("div", {
        className: styles.discount
      }, /*#__PURE__*/React.createElement(DiscountTextField, _extends({
        size: "small",
        "data-testid": "add-charge-".concat(itemIndex, "-discount"),
        disabled: !service
      }, connectControllerToTextField(renderProps), {
        value: discount,
        onChange: _ref => {
          let {
            type,
            amount
          } = _ref;
          const didTypeChange = type !== discount.type;
          if (didTypeChange) {
            renderProps.field.onChange({
              type,
              amount: ''
            });
          } else {
            renderProps.field.onChange({
              type,
              amount
            });
          }
        }
      })))
    })
  }, {
    value: total != null ? /*#__PURE__*/React.createElement(Typography, {
      "data-testid": "add-charge-total",
      skin: total.lessThan(0) ? 'error' : 'standard'
    }, utils.formatDollarAmount(total)) : null
  }];
  if (serviceTemplatesError) throw serviceTemplatesError;
  return /*#__PURE__*/React.createElement(BillTableItem, {
    columnDefs: columnDefs,
    headerItems: headerItems,
    rowItems: rowItems,
    headerTestId: "add-charge-table-header",
    newUI: newUI,
    actionColumn: actionColumn,
    footer: newUI && /*#__PURE__*/React.createElement("div", {
      className: styles.noteAndCalloutsContainer
    }, /*#__PURE__*/React.createElement(BillingItemNote, {
      testid: "manual-bill-note-".concat(itemIndex),
      name: "items.".concat(itemIndex, ".note")
    }), /*#__PURE__*/React.createElement(BillChargeClientApprovalCallout, {
      issueDate: issueDate,
      invoiceDateInfo: invoiceDateInfo
    }))
  });
}